import { useState, useEffect, useMemo, useCallback } from 'react';
import { sanitizeSearchInput } from '../utils/security';
import { fetchCategories } from '../services/categoriesService';
import { fetchAllContentsByPaginating } from '../services/productsService';
import {
  buildProductCategoryTree,
  foldedSearchTokens,
  productMatchesFoldedTokens,
} from '../utils/productPickerCore';

export const DRAFT_CAT = '__draft__';

export const getCategoryId = (c) => String(c?.id || c?._id || '');

export function collectDescendantIds(categoryId, flatCategories) {
  const ids = new Set([String(categoryId)]);
  const children = flatCategories.filter(
    (c) => c.ustKategoriId && String(c.ustKategoriId) === String(categoryId),
  );
  for (const child of children) {
    collectDescendantIds(getCategoryId(child), flatCategories).forEach((id) => ids.add(id));
  }
  return ids;
}

export const getContentName = (p) => p.icerikAdi || p.parcaAdi || p.urunAdi || p.ad || '—';
export const getContentCode = (p) => p.icerikKodu || p.parcaKodu || p.urunKodu || p.kod || '—';

/** Kategori ağacını düz select seçenekleri listesine çevirir */
export function flattenCategoryOptions(tree, depth = 0) {
  const out = [];
  for (const node of tree || []) {
    const prefix = depth > 0 ? `${'— '.repeat(depth)}` : '';
    out.push({
      id: node.cid,
      label: `${prefix}${node.kategoriAdi || '—'}`,
    });
    if (node.children?.length) {
      out.push(...flattenCategoryOptions(node.children, depth + 1));
    }
  }
  return out;
}

/**
 * Sipariş parça kataloğu: kategoriler, parçalar, arama ve filtreleme.
 * @param {{ secilenParcalar?: object[], enableDraftFilter?: boolean, eslesenUrunIcerikId?: string|null }} options
 */
export function useSiparisParcaCatalog({
  secilenParcalar = [],
  enableDraftFilter = false,
  eslesenUrunIcerikId = null,
} = {}) {
  const [categories, setCategories] = useState([]);
  const [allParcalar, setAllParcalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});
  const [parcaArama, setParcaArama] = useState('');
  const [parcaAramaDebounced, setParcaAramaDebounced] = useState('');
  const [qtyById, setQtyById] = useState({});

  useEffect(() => {
    const t = setTimeout(() => {
      setParcaAramaDebounced(sanitizeSearchInput(parcaArama, { trimEdges: false }) || '');
    }, 300);
    return () => clearTimeout(t);
  }, [parcaArama]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const parcaFilters = { icerikTipi: 'parca', aktif: true };
    if (eslesenUrunIcerikId) {
      parcaFilters.eslesenUrunIcerikId = eslesenUrunIcerikId;
    }
    Promise.all([
      fetchCategories({ kategoriTipi: 'parca' }),
      fetchAllContentsByPaginating(parcaFilters),
    ])
      .then(([catRes, parcaList]) => {
        if (cancelled) return;
        const cats = catRes?.veri?.kategoriler || catRes?.veri || [];
        setCategories(Array.isArray(cats) ? cats : []);
        setAllParcalar(Array.isArray(parcaList) ? parcaList : []);
      })
      .catch(() => {
        if (!cancelled) {
          setCategories([]);
          setAllParcalar([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eslesenUrunIcerikId]);

  const categoryTree = useMemo(() => buildProductCategoryTree(categories), [categories]);

  const categoryOptions = useMemo(
    () => flattenCategoryOptions(categoryTree),
    [categoryTree],
  );

  useEffect(() => {
    if (categoryTree.length === 0) return;
    setExpandedIds((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next = {};
      categoryTree.forEach((r) => {
        next[r.cid] = true;
      });
      return next;
    });
  }, [categoryTree]);

  const filteredParcalar = useMemo(() => {
    let list = allParcalar;
    const tokens = foldedSearchTokens(parcaAramaDebounced);
    if (tokens.length) {
      list = list.filter((p) => productMatchesFoldedTokens(p, tokens));
    }
    if (enableDraftFilter && selectedCategoryId === DRAFT_CAT) {
      const ids = new Set(
        (secilenParcalar || []).map((p) => String(p.icerikId || p.id || p._id)),
      );
      list = list.filter((p) => ids.has(String(p.id || p._id)));
    } else if (selectedCategoryId && selectedCategoryId !== DRAFT_CAT) {
      const allowed = collectDescendantIds(selectedCategoryId, categories);
      list = list.filter((p) => allowed.has(String(p.kategoriId || '')));
    }
    return [...list].sort((a, b) =>
      getContentName(a).localeCompare(getContentName(b), 'tr', { sensitivity: 'base' }),
    );
  }, [
    allParcalar,
    parcaAramaDebounced,
    selectedCategoryId,
    categories,
    secilenParcalar,
    enableDraftFilter,
  ]);

  const selectedCategoryLabel = useMemo(() => {
    if (!selectedCategoryId || selectedCategoryId === DRAFT_CAT) return null;
    const cat = categories.find((c) => getCategoryId(c) === selectedCategoryId);
    return cat?.kategoriAdi || 'Kategori';
  }, [selectedCategoryId, categories]);

  const clearCategoryFilter = useCallback(() => setSelectedCategoryId(null), []);

  const toggleExpand = useCallback((cid, e) => {
    e.stopPropagation();
    setExpandedIds((prev) => ({ ...prev, [cid]: !prev[cid] }));
  }, []);

  const selectCategory = useCallback((cid) => {
    setSelectedCategoryId(cid);
  }, []);

  const selectedPartIds = useMemo(
    () =>
      new Set(
        (secilenParcalar || []).map((p) => String(p.icerikId || p.id || p._id)),
      ),
    [secilenParcalar],
  );

  return {
    categories,
    allParcalar,
    loading,
    selectedCategoryId,
    setSelectedCategoryId,
    clearCategoryFilter,
    expandedIds,
    toggleExpand,
    selectCategory,
    categoryTree,
    categoryOptions,
    selectedCategoryLabel,
    parcaArama,
    setParcaArama,
    parcaAramaDebounced,
    qtyById,
    setQtyById,
    filteredParcalar,
    selectedPartIds,
  };
}
